import { useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppShell,
  makeSsoAppNavigation,
  type InAppTab,
  type NavItem,
  type SsoAppNavigation,
} from '@kawai/app-shell';
import {
  buildSsoSessionKey,
  resolveManagementAppLinks,
  resolvePortalUrl,
  type SsoSessionInput,
} from '@kawai/shared';
import { useAuth } from '../lib/auth.js';
import { useTenantBrand } from '../lib/tenantBrand.js';
import { callIssueSsoToken } from '../lib/api/issueSsoToken.js';
import { ROUTES } from '../routes.js';

const NAV_ITEMS: readonly NavItem[] = [
  { to: ROUTES.customers, label: '顧客', end: true },
  // M-161: 分析サマリー（M-159）を顧客管理の上段から独立ページへ（運用者指示 2026-10-09）。
  { to: ROUTES.analytics, label: '分析サマリー' },
  { to: ROUTES.hotLeads, label: 'HOT案件' },
  { to: ROUTES.plans, label: '自動配信設定' },
  // M-163: KPI（`/kpi`）を自動配信設定の直下に出す（運用者指示 2026-10-10 / 2026-09-21 の
  // 「メニューに出さない」を撤回）。自動配信設定からの「配信の効果（KPI）を見る →」は残す。
  { to: ROUTES.kpi, label: 'KPI' },
  // M-94: プラン配下だけだったアンケートを独立メニュー化（作成・編集はプレビュー付きページ）。
  { to: ROUTES.surveys, label: 'アンケート' },
  // M-124: 初回診断アンケート（4 問の可視化 + 設定 + 定期サイクルの実行）。
  { to: ROUTES.diagnosis, label: '診断アンケート' },
  { to: ROUTES.segmentCompose, label: 'セグメント配信' },
  { to: ROUTES.scheduledPushes, label: '予約配信' },
  { to: ROUTES.campaigns, label: '配信履歴' },
  { to: ROUTES.coupons, label: 'クーポン' },
  { to: ROUTES.rewardCoupons, label: '予約特典クーポン' },
  // 読み物。他の項目がデータ表示・編集画面なので最下部に置く。
  { to: ROUTES.engineGuide, label: '確度エンジンの仕組み' },
] as const;

/**
 * M-164: 上部タブ「イベント」配下の左メニュー（`/event/*` で NAV_ITEMS と入れ替える）。
 * 感謝祭のイベント 1 件のみを扱う（イベント一覧 / 新規作成は感謝祭後の課題）。
 */
const EVENT_NAV_ITEMS: readonly NavItem[] = [
  { to: ROUTES.eventSettings, label: 'ルーレット設定' },
  { to: ROUTES.eventCoupons, label: '当選クーポン' },
  { to: ROUTES.eventStatus, label: '抽選状況' },
] as const;

/** `/event` と `/event/...` だけをイベントタブの現在地とみなす（`/events-x` 等は対象外）。 */
export function isEventTabPath(pathname: string): boolean {
  return pathname === '/event' || pathname.startsWith('/event/');
}

/**
 * tenant-promo app の共通レイアウト（LE-00 / promo feature）。
 *
 * T-10: 共通シェル `@kawai/app-shell` の `<AppShell>` に NAV_ITEMS と app 固有の DI を
 * 渡す薄い wrapper。nav の aria-label は AppShell 既定の「サイドバーメニュー」に統一
 * （旧「販促管理ナビゲーション」を T-09b 確定の文言へ収束 / T-10 受け入れ条件）。
 *
 * A-37: 旧「ポータルへ」ボタンを廃止し、契約済みの各管理画面（予約 / 燃料 / 販促）への
 * 直接リンクをヘッダーに表示する（`resolveManagementAppLinks`）。
 */
export function Layout() {
  const { user, role, tenantId, features, signOut } = useAuth();
  // A-34 / S-10: テナントブランド（brandName=セルフ武生21SS / logoUrl / primaryColor）を
  // sticky ヘッダに表示。Layout は AuthGuard 配下で 1 度だけマウントされるため fetch は
  // 起動時 1 回のみ。4 管理アプリでヘッダーを統一する。
  const { brand } = useTenantBrand(tenantId);

  // S-91: 現セッションを毎 render で ref に反映し、navigation の getSessionKey が最新の
  // uid + claims fingerprint を読めるようにする（トークンキャッシュ・素リンク確立記録の
  // 両方がこのキー一致で守られ、サインアウト / ユーザー切替 / impersonation を跨いだ再利用を防ぐ）。
  const sessionRef = useRef<SsoSessionInput>({
    uid: null,
    role: null,
    tenantId: null,
    features: [],
  });
  sessionRef.current = { uid: user?.uid ?? null, role, tenantId, features };
  const navRef = useRef<SsoAppNavigation | null>(null);
  if (navRef.current === null) {
    navRef.current = makeSsoAppNavigation(
      (targetApp, { prefetch }) => callIssueSsoToken({ targetApp, prefetch }),
      { getSessionKey: () => buildSsoSessionKey(sessionRef.current) },
    );
  }
  const ssoNavigation = navRef.current;
  const appLinks = resolveManagementAppLinks(features, 'promo', import.meta.env.MODE);
  // M-164: 「イベント」は別アプリにせず同一 SPA 内のタブ（SSO・hosting を増やさない）。
  // 顧客/販促タブは `/event/*` にいる間は現在地でなくなるので、クリックで `/` へ戻す。
  const location = useLocation();
  const navigate = useNavigate();
  const onEvent = isEventTabPath(location.pathname);
  const eventTab: InAppTab = {
    key: 'event',
    label: 'イベント',
    to: ROUTES.eventStatus,
    isCurrent: onEvent,
    onSelect: () => navigate(ROUTES.eventStatus),
  };
  const promoLinks = appLinks.map((link) =>
    link.feature === 'promo' && onEvent ? { ...link, isCurrent: false } : link,
  );

  return (
    <AppShell
      navItems={onEvent ? EVENT_NAV_ITEMS : NAV_ITEMS}
      inAppTabs={[eventTab]}
      brand={brand}
      appLinks={promoLinks}
      onNavigateApp={(link) => {
        // 自 app（顧客/販促）のタブはイベントタブから戻るときだけ押せる = 同一 SPA 内遷移。
        if (link.feature === 'promo') {
          navigate(ROUTES.customers);
          return;
        }
        ssoNavigation.onNavigateApp(link);
      }}
      onPrefetchApp={ssoNavigation.onPrefetchApp}
      prerenderUrls={ssoNavigation.selectPrerenderUrls(appLinks)}
      portalUrl={resolvePortalUrl(import.meta.env.MODE)}
      onSignOut={() => {
        ssoNavigation.clearAllEstablished();
        void signOut();
      }}
    />
  );
}
